import { inject, Injectable } from '@angular/core';
import {
  FileInfo,
  OptionInfo,
  OptionVersion,
  PlatformConfig,
  PlatformRedirection,
  ProjectSubsection,
  ShareLink,
  ShareLinkAsset,
  ShareLinkAssetEntityTypes,
  ShareLinkSimple,
  ShareListResponse,
  SubsectionNavigationParams,
  TopicInfo,
  TypedPlatformConfig,
} from '../../shared/interfaces';
import { ProjectService } from '../../shared/services/project.service';
import { v4 as uuid4 } from 'uuid';
import { AuthService } from '../../shared/services/auth.service';
import { BehaviorSubject, map, Observable, Subject } from 'rxjs';
import { ApiService } from '../../shared/services/api.service';
import { BaseTreeService } from './base-tree.service';
import {
  isShareLinkAssetNode,
  isShareLinkItemNode,
  isShareLinkNode,
  ShareLinkAssetNode,
  ShareLinkItemNode,
  ShareLinkNode,
  ShareLinkNodeData,
  ShareLinkRootNode,
  TreeNodeType,
} from '../models/tree-node';
import { TreeSortMode } from '../../shared/models';
import { takeUntil, tap } from 'rxjs/operators';
import moment from 'moment';
import {
  getShareLinkAssetFromVM,
  prepareShareLinkAsset,
  prepareShareLinkAssetsData,
} from '../utilities/share-link-utilities';
import { getVersionId } from '../utilities/subsection-utilities';
import {
  BuildShareLinkTreeParams,
  SendMailBodyParams,
  ShareLinkAssetVM,
  ShareLinkRequestParams,
  ShareLinkTreeState,
} from '../models';
import { GeneralUtilsService } from '../../shared/services/general-utils.service';

@Injectable({
  providedIn: 'root',
})
export class ShareLinkTreeService extends BaseTreeService<ShareLinkNodeData> {
  private readonly projectService = inject(ProjectService);
  private readonly authService = inject(AuthService);
  private readonly apiService = inject(ApiService);
  private readonly generalUtilsService = inject(GeneralUtilsService);
  private readonly detailLoading$ = new Subject<void>();

  protected override state: ShareLinkTreeState = {
    root: null,
    paths: {},
    selectedNode: null,
    expandedNodes: [],
    loading: false,
    shareRecord: {},
  };
  private previousShareLinkNode: ShareLinkItemNode | null = null;

  protected override state$ = new BehaviorSubject<ShareLinkTreeState>(this.state);

  override getState(): Observable<ShareLinkTreeState> {
    return this.state$.asObservable();
  }

  override updateState(update: Partial<ShareLinkTreeState>) {
    this.state = {
      ...this.state,
      ...update,
    };
    this.state$.next(this.state);
  }

  getSubsectionShareLinks(projectId: string, sectionId: string, subsectionId: string): Observable<ShareListResponse> {
    return this.apiService
      .httpGet(`/api/sharelink/project/${projectId}/section/${sectionId}/subsection/${subsectionId}`)
      .pipe(
        map((response: any) => ({
          shareLinks: response.shareLinks || [],
          reviewLinkMap: response.reviewLinkMap || {},
          reviewLinkRecord: response.reviewLinkRecord || {},
        }))
      );
  }

  //use this is share link page
  followShareLink(shareLinkId: string, params: ShareLinkRequestParams): Observable<ShareLink> {
    return this.apiService
      .httpPost(`/api/sharelink/${shareLinkId}`, params)
      .pipe(map((response: any) => response as ShareLink));
  }

  getShareLinkDetail(params: {
    projectId: string;
    sectionId: string;
    subsectionId: string;
    shareLinkId: string;
  }): Observable<ShareLink> {
    const { projectId, sectionId, subsectionId, shareLinkId } = params;
    const url = `/api/sharelink/project/${projectId}/section/${sectionId}/subsection/${subsectionId}/${shareLinkId}`;
    return this.apiService.httpGet(url).pipe(map((response: any) => response as ShareLink));
  }

  addShareLinkAssetFromEntity(projectId: string, shareLinkId: string, data: any): Observable<ShareLink> {
    return this.apiService
      .httpPost(`/api/sharelink/project/${projectId}/${shareLinkId}/asset/add`, data)
      .pipe(map((response: any) => response as ShareLink));
  }

  removeShareLinkAssetFromEntity(projectId: string, shareLinkId: string, data: any) {
    return this.apiService
      .httpPost(`/api/sharelink/project/${projectId}/${shareLinkId}/asset/remove`, data)
      .pipe(map((response: any) => response as ShareLink));
  }

  prepareAssets(shareLinks: ShareLink[]) {
    shareLinks.forEach((shareLink) => {
      if (!shareLink.assets?.length) {
        return;
      }

      prepareShareLinkAssetsData(shareLink);
    });
  }

  buildTree(params: BuildShareLinkTreeParams) {
    const { list, sortMode, topics, options } = params;

    const [shareRecord] = this.populateShareRecord(list);

    this.prepareAssets(list.shareLinks);
    const rootNode: ShareLinkRootNode = {
      id: uuid4(),
      name: 'All Share Links',
      type: TreeNodeType.ShareLinkRoot,
      isExpandable: true,
      isSelected: true,
      isExpanded: true,
      children: [] as ShareLinkItemNode[],
      parent: null,
      depth: 0,
    };

    this.state.paths = {
      [rootNode.id]: [rootNode.id],
    };

    this.prepareAssets(list.shareLinks);
    const shareLinks = list.shareLinks || [];
    shareLinks.forEach((shareLink) => {
      const shareLinkNode: ShareLinkItemNode = {
        id: shareLink.id,
        name: shareLink.title,
        type: TreeNodeType.ShareLinkItem,
        data: shareLink,
        isExpandable: true,
        isSelected: false,
        isExpanded: false,
        children: [] as ShareLinkAssetNode[],
        parent: rootNode,
        depth: 1,
      };

      this.addNodeToParent(rootNode, shareLinkNode);
    });

    this.state = {
      ...this.state,
      root: rootNode,
      shareRecord,
    };

    this.updateState(this.state);
    this.sortTree(sortMode, true);
  }

  sortTree(sortMode: TreeSortMode, updateState?: boolean) {
    if (!this.state.root) return;
    this.sortNode(this.state.root as ShareLinkItemNode, sortMode);
    if (updateState) {
      this.updateState({
        root: this.state.root,
      });
    }
  }

  private sortNode(node: ShareLinkNode, sortMode: TreeSortMode) {
    if (!node) return;
    this.sortChildren(node, sortMode);

    node.children.forEach((child) => {
      if (isShareLinkNode(child)) {
        this.sortNode(child, sortMode);
      }
    });
  }

  private sortChildren(node: ShareLinkNode, sortMode: TreeSortMode) {
    if (
      !node.children?.length ||
      (isShareLinkAssetNode(node.children[0]) && node.children[0].assetType == ShareLinkAssetEntityTypes.VERSION)
    ) {
      return;
    }

    node.children.sort((a, b) => {
      if (isShareLinkItemNode(a)) {
        const shareLinkA = a as ShareLinkItemNode;
        const shareLinkB = b as ShareLinkItemNode;

        switch (sortMode) {
          case TreeSortMode.lastUpdated:
          case TreeSortMode.lastUpload:
            return (shareLinkB.data.updatedAt || 0) - (shareLinkA.data.updatedAt || 0);
          case TreeSortMode.firstUpload:
            return (shareLinkA.data.createdAt || 0) - (shareLinkB.data.createdAt || 0);
          default:
            return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
        }
      } else if (isShareLinkAssetNode(a)) {
        const assetA = a as ShareLinkAssetNode;
        const assetB = b as ShareLinkAssetNode;

        switch (sortMode) {
          case TreeSortMode.lastUpload:
            return (assetB.data.lastUploaded || 0) - (assetA.data.lastUploaded || 0);
          case TreeSortMode.firstUpload:
            return (assetA.data.firstUploaded || 0) - (assetB.data.firstUploaded || 0);
          case TreeSortMode.lastUpdated:
            return (assetB.data.updatedAt || 0) - (assetA.data.updatedAt || 0);
          default:
            return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
        }
      } else {
        return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
      }
    });
  }

  private populateShareRecord(
    response: ShareListResponse
  ): [Record<string, ShareLinkSimple[]>, Record<string, number>] {
    const shareRecord: Record<string, ShareLinkSimple[]> = {};
    const shareCountRecord: Record<string, number> = {};
    const reviewLinkMap = response.reviewLinkMap || {};
    Object.entries(reviewLinkMap).forEach(([assetId, shareIndexes]) => {
      if (!(Array.isArray(shareIndexes) && shareIndexes.length > 0)) {
        return;
      }
      const realId = assetId.split('-').splice(1).join('-');

      shareRecord[realId] = [];
      shareIndexes.forEach((index) => {
        if (index >= response.shareLinks.length) {
          return;
        }
        shareRecord[realId].push({
          id: response.shareLinks[index].id,
          title: response.shareLinks[index].title,
        });
      });
      shareCountRecord[realId] = shareRecord[realId].length;
    });

    return [shareRecord, shareCountRecord];
  }

  override clearState() {
    this.updateState({
      root: null,
      paths: {},
      selectedNode: null,
      expandedNodes: [],
      shareRecord: {},
      loading: false,
    });
  }

  createEmptyShareLink(args: { projectId: string; sectionId: string; subsectionId: string }): Observable<ShareLink> {
    const { projectId, sectionId, subsectionId } = args;
    const proposedName = `Share Link ` + moment().format('D MMM YYYY');
    const newName = this.getShareLinkTitle(proposedName);
    const newLink: ShareLink = {
      id: uuid4(),
      title: newName,
      projectId,
      sectionId,
      subsectionId,
      commentEnabled: true,
      creator: this.authService.getAuthUser().username,
      companyId: this.authService.getAuthUser().companyId,
      createdAt: Date.now(),
      participants: [],
      blacklistedParticipants: [],
      assets: [],
      isRestricted: false,
    };

    return this.saveShareLink(newLink).pipe(
      tap((data) => {
        prepareShareLinkAssetsData(data);
        this.addShareLinkNode(data);
      })
    );
  }

  createNewShareFromAssetVM(input: ShareLinkAssetVM): Observable<ShareLink> {
    const sectionId = input.subsection!.projectSectionId;
    const subsectionId = input.subsection!.id;
    const asset = getShareLinkAssetFromVM(input);
    const newName = this.getShareLinkTitle(asset.name);
    const newLink: ShareLink = {
      id: uuid4(),
      title: newName,
      projectId: this.projectService.project.id,
      sectionId,
      subsectionId,
      commentEnabled: true,
      creator: this.authService.getAuthUser().username,
      companyId: this.authService.getAuthUser().companyId,
      createdAt: Date.now(),
      participants: [],
      blacklistedParticipants: [],
      assets: [asset],
      isRestricted: false,
    };

    return this.saveShareLink(newLink).pipe(
      tap((data) => {
        prepareShareLinkAssetsData(data);
        this.addShareLinkNode(data);
      })
    );
  }

  private getShareLinkTitle(name: string): string {
    let counter = 0;
    let nameToCheck = name;
    let similarNameIndex = this.state.root.children.findIndex((c) => c.name == name);
    while (similarNameIndex > -1) {
      counter++;
      nameToCheck = `${name} - ${counter}`;
      similarNameIndex = this.state.root.children.findIndex((c) => c.name == nameToCheck);
    }
    return nameToCheck;
  }

  saveShareLink(shareLink: ShareLink): Observable<ShareLink> {
    return this.apiService
      .httpPut(`/api/sharelink/project/${shareLink.projectId}`, shareLink)
      .pipe(map((response: any) => response as ShareLink));
  }

  resendShareLinkEmail(projectId: string, shareLinkId: string, body: SendMailBodyParams): Observable<ShareLink> {
    return this.apiService
      .httpPost(`/api/sharelink/project/${projectId}/${shareLinkId}/mail`, body)
      .pipe(map((response: any) => response as ShareLink));
  }

  deleteShareLink(projectId: string, shareLinkId: string) {
    return this.apiService.httpDelete(`/api/sharelink/project/${projectId}/${shareLinkId}`);
  }

  override setActiveNode(node: ShareLinkNodeData, saveState: boolean = true) {
    super.setActiveNode(node, saveState);
  }

  buildAssetNode(params: {
    asset: ShareLinkAsset;
    parent: ShareLinkItemNode | ShareLinkAssetNode;
    topics?: TopicInfo[];
    options?: OptionInfo[];
    subsection?: ProjectSubsection;
    sortMode?: TreeSortMode;
  }): ShareLinkAssetNode {
    const { asset, parent, topics, options, sortMode, subsection } = params;
    prepareShareLinkAsset(asset);
    if (asset.entityType == ShareLinkAssetEntityTypes.SUBFOLDER) {
      const option = options?.find((o) => o.optionId == asset.optionId);
      if (option?.simpleVersions?.length === 1) {
        asset.singleVersionNumber = option.simpleVersions[0].versionNumber;
        asset.versionId = getVersionId(asset.optionId, asset.singleVersionNumber);
      }
    } else if (asset.entityType == ShareLinkAssetEntityTypes.VERSION) {
      asset.versionId = asset.itemId;
    }

    const assetNode: ShareLinkAssetNode = {
      id: asset.itemId,
      name: asset.name,
      type: TreeNodeType.ShareLinkAsset,
      isExpandable: true,
      isSelected: false,
      isExpanded: false,
      children: [],
      parent: parent,
      depth: parent.depth + 1,
      assetType: asset.entityType,
      data: asset,
    };

    if (asset.entityType == ShareLinkAssetEntityTypes.VERSION) {
      assetNode.name = `Version ${asset.version}`;
      asset.name = assetNode.name;
    }

    this.addNodeToParent(parent, assetNode);

    if (asset.children) {
      if (
        asset.entityType == ShareLinkAssetEntityTypes.SUBFOLDER &&
        asset.children.length == 1 &&
        asset.children[0].children.length > 0
      ) {
        asset.singleVersionNumber = asset.children[0].version;
        asset.versionId = getVersionId(asset.optionId, asset.singleVersionNumber);

        asset.children[0].children.forEach((child) => {
          this.buildAssetNode({
            asset: child,
            parent: assetNode,
            topics,
            options,
            subsection,
            sortMode,
          });
        });
      } else {
        if (asset.entityType == ShareLinkAssetEntityTypes.VERSION) {
          asset.versionId = asset.itemId;
        }
        asset.children.forEach((child) => {
          this.buildAssetNode({
            asset: child,
            parent: assetNode,
            topics,
            options,
            subsection,
            sortMode,
          });
        });
      }
    }

    return assetNode;
  }

  private clearShareLinkAssetNodes(link: ShareLinkItemNode) {
    for (let i = link.children.length - 1; i >= 0; i--) {
      this.deleteNode(link.children[i] as ShareLinkAssetNode);
    }
    link.children = [];
  }

  setActiveRoot() {
    this.setActiveNode(this.state.root, true);
  }

  setActiveShareLinkById(params: {
    id: string;
    topics?: TopicInfo[];
    options?: OptionInfo[];
    subsection?: ProjectSubsection;
    sortMode?: TreeSortMode;
    assetId?: string;
  }) {
    const { id, topics, options, subsection, sortMode, assetId } = params;
    const node = this.findNodeById(id) as ShareLinkItemNode;
    if (!node) {
      return;
    }

    this.setActiveShareLink({
      node,
      topics: topics || [],
      options: options || [],
      subsection: subsection,
      sortMode: sortMode,
      assetId,
    });
  }

  setActiveShareLink(params: {
    node: ShareLinkItemNode;
    topics: TopicInfo[];
    options: OptionInfo[];
    subsection: ProjectSubsection;
    sortMode: TreeSortMode;
    assetId?: string;
  }) {
    const { node, topics, options, subsection, sortMode, assetId } = params;

    // clear if switching to a different node
    if (this.previousShareLinkNode && this.previousShareLinkNode !== node) {
      this.clearShareLinkAssetNodes(this.previousShareLinkNode);
      this.previousShareLinkNode = node;
    } else if (!this.previousShareLinkNode) {
      this.previousShareLinkNode = node;
    }

    if (!assetId || node.children.length == 0) {
      this.setActiveNode(node, true);
    }

    if (!!node.data.tree && node.children.length == 0) {
      this.detailLoading$.next();
      this.updateState({
        loading: true,
      });
      this.getShareLinkDetail({
        projectId: node.data.projectId,
        sectionId: node.data.sectionId,
        subsectionId: node.data.subsectionId,
        shareLinkId: node.id,
      })
        .pipe(takeUntil(this.detailLoading$))
        .subscribe({
          next: (data: ShareLink) => {
            prepareShareLinkAssetsData(data);
            node.data = data;
            if (data.tree.length > 0) {
              this.buildAssetNode({
                asset: data.tree[0],
                parent: node,
                topics,
                options,
                subsection,
                sortMode,
              });
            }

            if (assetId) {
              this.setActiveNodeById(assetId);
            }

            this.updateState({
              loading: false,
            });
          },
          error: (err) => {
            this.updateState({
              loading: false,
            });
          },
        });
    } else if (assetId) {
      this.setActiveNodeById(assetId);
    }
  }

  addShareLinkNode(shareLink: ShareLink) {
    const parentNode = this.state.root;

    const node: ShareLinkItemNode = {
      id: shareLink.id,
      name: shareLink.title,
      type: TreeNodeType.ShareLinkItem,
      data: shareLink,
      children: [],
      parent: parentNode,
      isExpandable: true,
      depth: parentNode.depth + 1,
    };
    this.addNodeToParent(parentNode, node);
    this.adjustShareRecord(node, false, true);
    return node;
  }

  updateShareLinkNode(params: {
    shareLink: ShareLink;
    topics?: TopicInfo[];
    options?: OptionInfo[];
    subsection?: ProjectSubsection;
    sortMode?: TreeSortMode;
  }) {
    const { shareLink, topics, options, subsection, sortMode } = params;
    const node = this.findNodeById(shareLink.id) as ShareLinkItemNode;
    if (!node) {
      return;
    }

    node.data = shareLink;
    node.name = shareLink.title;
    node.lastUpdated = shareLink.updatedAt;
    this.clearShareLinkAssetNodes(node);
    if (shareLink.tree?.length > 0) {
      this.buildAssetNode({
        asset: shareLink.tree[0],
        parent: node,
        topics,
        options,
        subsection,
        sortMode,
      });
    }
    this.adjustShareRecord(node, true, true);
  }

  deleteShareLinkNode(node: ShareLinkItemNode) {
    this.adjustShareRecord(node, true, false);
    this.deleteNode(node);
  }

  deleteShareLinkNodeById(id: string) {
    const node = this.findNodeById(id) as ShareLinkItemNode;
    if (!node) {
      return;
    }
    this.deleteShareLinkNode(node);
  }

  adjustShareRecord(itemNode: ShareLinkItemNode, removeRelated: boolean, addRelated: boolean) {
    const nodeId = itemNode.id;
    // Create a new shareRecord object
    const newShareRecord = { ...this.state.shareRecord };

    //remove related
    if (removeRelated) {
      Object.entries(newShareRecord).forEach(([itemId, shareLinks]) => {
        const index = shareLinks.findIndex((s) => s.id === nodeId);
        if (index > -1) {
          // Create a new array for this itemId
          newShareRecord[itemId] = [...shareLinks];
          newShareRecord[itemId].splice(index, 1);
        }
      });
    }

    //add related
    const assetSimpleInfo = {
      id: itemNode.id,
      title: itemNode.name,
    };

    if (addRelated) {
      itemNode.data.assets.forEach((asset) => {
        const assetId = asset.itemId;
        if (!newShareRecord[assetId]) {
          newShareRecord[assetId] = [];
        }
        // Create a new array for this assetId
        newShareRecord[assetId] = [...newShareRecord[assetId], assetSimpleInfo];
      });
    }

    this.updateState({
      shareRecord: newShareRecord,
    });
  }

  copyLinkById(shareId: string) {
    const node = this.findNodeById(shareId) as ShareLinkItemNode;

    if (!node) {
      return;
    }

    this.generalUtilsService.copyToClipboard(node.data.url);
  }

  getAssetNavigationParams(asset: ShareLinkAsset, sectionId: string): SubsectionNavigationParams {
    const params: SubsectionNavigationParams = {
      projectId: this.projectService.project?.id,
      sectionId,
      subsectionId: asset.subSectionId,
      navigationType: 'subsection',
    };

    switch (asset.entityType) {
      case ShareLinkAssetEntityTypes.FOLDER:
        params.topicId = asset.topicId;
        break;
      case ShareLinkAssetEntityTypes.SUBFOLDER:
        params.topicId = asset.topicId;
        params.optionId = asset.optionId;
        break;
      case ShareLinkAssetEntityTypes.VERSION:
        params.topicId = asset.topicId;
        params.optionId = asset.optionId;
        params.versionNumber = asset.version;
        break;
      case ShareLinkAssetEntityTypes.FILE:
        params.topicId = asset.topicId;
        params.optionId = asset.optionId;
        params.versionNumber = asset.version;
        params.fileId = asset.fileId;
        break;
    }

    return params;
  }

  populateShareLinkVersionFiles(params: {
    parent: ShareLinkAssetNode;
    option: OptionInfo;
    subsection: ProjectSubsection;
    version: OptionVersion;
    sortMode: TreeSortMode;
  }) {
    const { parent, version, option, subsection, sortMode } = params;
    const versionSimple = option.simpleVersions.find((v) => v.versionNumber == version.versionNumber);

    for (let i = parent.children.length - 1; i >= 0; i--) {
      this.deleteNode(parent.children[i] as ShareLinkAssetNode);
    }

    version.files.forEach((file: FileInfo) => {
      const asset = getShareLinkAssetFromVM({
        file,
        option,
        version: versionSimple,
        type: ShareLinkAssetEntityTypes.FILE,
        subsection,
      });
      const assetNode: ShareLinkAssetNode = {
        id: asset.itemId,
        name: asset.name,
        type: TreeNodeType.ShareLinkAsset,
        isExpandable: false,
        isSelected: false,
        isExpanded: false,
        children: [],
        parent: parent,
        depth: parent.depth + 1,
        assetType: asset.entityType,
        data: asset,
      };
      this.addNodeToParent(parent, assetNode);
    });
  }

  checkShareLinkRedirection(linkId: string): Observable<TypedPlatformConfig<PlatformRedirection> | null> {
    return this.apiService.httpGet(`/api/sharelink/${linkId}`).pipe(
      map((result: any) => {
        if (result == null) {
          return null;
        }

        const res = result as PlatformConfig;

        try {
          const configValue = JSON.parse(res.configValue) as PlatformRedirection;
          return {
            ...res,
            data: configValue,
          };
        } catch (error) {
          return null;
        }
      })
    );
  }
}
